import React, { useState, useEffect } from 'react';
import { studentsAPI, usersAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { FiPlus, FiEdit, FiTrash2, FiSearch, FiAward } from 'react-icons/fi';
import AlumniModal from './AlumniModal';
import './Alumni.css';

const Alumni = () => {
  const { isAdmin, isEmployee, isSalesLeadership } = useAuth();
  // Same set as the alumni API: admins, permanent employees and sales leadership (Chairman, CSM).
  const canAccessAlumni = () => isAdmin() || isEmployee() || isSalesLeadership();
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedAlumni, setSelectedAlumni] = useState(null);
  const [agents, setAgents] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    if (canAccessAlumni()) {
      fetchAlumni();
      fetchAgentsAndEmployees();
    }
  }, [page, isAdmin, isEmployee, isSalesLeadership]);

  const fetchAlumni = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        search: searchTerm || undefined,
      };
      const response = await studentsAPI.listAlumni(params);
      
      // Safely handle response - check if response exists first
      if (!response || !response.data) {
        setAlumni([]);
        setTotalPages(1);
        return;
      }
      
      // Handle both paginated and non-paginated responses
      // DRF returns results array for paginated responses, or array directly for non-paginated
      const alumniData = Array.isArray(response.data) 
        ? response.data 
        : (response.data.results || []);
      
      setAlumni(Array.isArray(alumniData) ? alumniData : []);
      
      // Handle pagination
      if (response.data.count !== undefined) {
        setTotalPages(Math.ceil(response.data.count / 50));
      } else {
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching alumni:', error);
      // Set empty state on any error (including 404 or when no alumni exist)
      setAlumni([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgentsAndEmployees = async () => {
    try {
      const [agentsResponse, employeesResponse] = await Promise.all([
        usersAPI.agents(),
        usersAPI.permanentEmployees(),
      ]);
      
      const agentsData = agentsResponse.data || [];
      const employeesData = employeesResponse.data || [];
      
      setAgents(agentsData);
      setEmployees(employeesData);
      
      // Combine agents and employees for assignment dropdown
      const combined = [
        ...agentsData.map(agent => ({ ...agent, displayName: `${agent.username} (Agent)` })),
        ...employeesData.map(emp => ({ ...emp, displayName: `${emp.username} (Employee)` }))
      ];
      setAssignableUsers(combined);
    } catch (error) {
      console.error('Error fetching agents and employees:', error);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (page === 1) {
        fetchAlumni();
      } else {
        setPage(1);
      }
    }, 500);

    return () => clearTimeout(debounce);
  }, [searchTerm]);

  const handleCreate = () => {
    setSelectedAlumni(null);
    setShowModal(true);
  };

  const handleEdit = (alumni) => {
    setSelectedAlumni(alumni);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this alumni?')) {
      try {
        await studentsAPI.deleteAlumni(id);
        fetchAlumni();
      } catch (error) {
        const errorMessage = error.response?.data?.error || error.response?.data?.detail || 'Error deleting alumni';
        alert(errorMessage);
        console.error('Error deleting alumni:', error);
      }
    }
  };

  if (!canAccessAlumni()) {
    return <div className="access-denied">Access denied. Alumni is available to admins, permanent employees and the CSM.</div>;
  }

  return (
    <div className="alumni-page">
      <div className="page-header">
        <div className="header-actions-left">
          <h2>
            <FiAward /> Alumni
          </h2>
          <button className="btn-primary" onClick={handleCreate}>
            <FiPlus /> Add Alumni
          </button>
        </div>
        <div className="header-actions-right">
          <div className="search-box">
            <FiSearch />
            <input
              type="text"
              placeholder="Search alumni..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading alumni...</div>
      ) : (
        <>
          <div className="alumni-table-container">
            <table className="alumni-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>University</th>
                  <th>Program</th>
                  <th>Graduation Year</th>
                  <th>Assigned Agent/Employee</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {alumni.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="empty-state">
                      No alumni found
                    </td>
                  </tr>
                ) : (
                  alumni.map((alum) => (
                    <tr key={alum.id}>
                      <td>
                        <strong>{alum.full_name || `${alum.first_name} ${alum.last_name}`}</strong>
                      </td>
                      <td>{alum.email}</td>
                      <td>{alum.phone || '-'}</td>
                      <td>{alum.university || '-'}</td>
                      <td>{alum.program || '-'}</td>
                      <td>{alum.graduation_year || '-'}</td>
                      <td>{alum.assigned_agent_name || alum.assigned_agent_detail?.username || '-'}</td>
                      <td>{new Date(alum.created_at).toLocaleDateString()}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            className="btn-icon"
                            onClick={() => handleEdit(alum)}
                            title="Edit"
                          >
                            <FiEdit />
                          </button>
                          <button
                            className="btn-icon btn-danger"
                            onClick={() => handleDelete(alum.id)}
                            title="Delete"
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </button>
              <span>Page {page} of {totalPages}</span>
              <button
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      {showModal && (
        <AlumniModal
          alumni={selectedAlumni}
          agents={assignableUsers}
          onClose={() => {
            setShowModal(false);
            setSelectedAlumni(null);
          }}
          onSave={() => {
            fetchAlumni();
            setShowModal(false);
            setSelectedAlumni(null);
          }}
        />
      )}
    </div>
  );
};

export default Alumni;

